/**
 * @fileoverview Single-tenant ("View Tenant") query hook.
 * @description Wraps GET /sales/tenants/:id in a TanStack query. Disabled while `id` is falsy, so
 * TenantDetailModal can call it unconditionally (passing the currently-viewed tenant's id, or an
 * empty string while the modal is closed) without an extra guard at the call site.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { getTenant } from '@/features/sales/services/tenants.service'

/**
 * @description Fetches one tenant's "View Tenant" detail payload.
 * @param {string} id - The tenant id to fetch, or an empty string to keep the query disabled.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useTenant(id: string) {
  return useQuery({
    queryKey: [...TENANTS_QUERY_KEY, 'detail', id],
    queryFn: () => getTenant(id),
    enabled: id !== '',
  })
}
